import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Avatar,
  Badge,
  Collapse,
  Divider,
  Chip
} from '@mui/material';
import {
  Close,
  ExpandMore,

} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useAuth } from '../../pages/authentication/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { menuConfig } from './menuconfig';
import moxylogo from "../../img/zaplogo.jpg"

// Animated Gradient Orb
const AnimatedOrb = ({ size = 200, top, left, color1, color2 }) => (
  <Box
    sx={{
      position: 'absolute',
      top,
      left,
      width: size,
      height: size,
      background: `radial-gradient(circle, ${color1}, ${color2})`,
      borderRadius: '50%',
      filter: 'blur(40px)',
      opacity: 0.4,
      animation: 'float 15s infinite ease-in-out',
      pointerEvents: 'none',
      zIndex: 0,
    }}
  />
);

// Glassmorphism Card Component
const GlassCard = ({ children, sx = {} }) => (
  <Box
    sx={{
      background: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.8)',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      ...sx
    }}
  >
    {children}
  </Box>
);

const MobileDrawer = ({ mobileDrawerOpen, toggleMobileDrawer }) => {
  const theme = useTheme();
  const [sidebarNavItems, setSidebarNavItems] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedPath, setSelectedPath] = useState('');
  const [expandedMenus, setExpandedMenus] = useState({});
  const [notificationsCount] = useState(5);

  const primary = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark;
  const primaryLight = theme.palette.primary.light;

  // === ROLE-BASED NAVIGATION WITH ROUTES ===
  useEffect(() => {
    const role = user?.role;
    setSidebarNavItems(menuConfig[role] || []);
  }, [user]);

  // Update selected path based on current location
  useEffect(() => {
    setSelectedPath(location.pathname);

    // Auto-expand parent menus if child is active
    sidebarNavItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => child.path === location.pathname);
        if (hasActiveChild) {
          setExpandedMenus(prev => ({ ...prev, [item.label]: true }));
        }
      }
    });
  }, [location.pathname, sidebarNavItems]);

  const toggleMenu = (label) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const handleItemClick = (item) => {
    if (item.children) {
      toggleMenu(item.label);
    } else if (item.path) {
      navigate(item.path);
      setSelectedPath(item.path);
      toggleMobileDrawer(); // Close drawer after navigation
    }
  };

  const handleChildClick = (childPath) => {
    navigate(childPath);
    setSelectedPath(childPath);
    toggleMobileDrawer(); // Close drawer after navigation
  };

  const isItemActive = (path) => {
    return selectedPath === path;
  };

  const isParentActive = (item) => {
    if (item.path && selectedPath === item.path) return true;
    if (item.children) {
      return item.children.some(child => child.path === selectedPath);
    }
    return false;
  };

  return (
    <>
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={toggleMobileDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: 300,
            background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
            borderRight: '1px solid rgba(0, 0, 0, 0.06)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {/* Animated Background Elements */}
          <AnimatedOrb size={300} top="-100px" left="-100px" color1={alpha(primary, 0.15)} color2="transparent" />
          <AnimatedOrb size={250} top="200px" left="100px" color1={alpha(primaryLight, 0.15)} color2="transparent" />
          <AnimatedOrb size={200} top="500px" left="-50px" color1={alpha(primaryDark, 0.15)} color2="transparent" />

          {/* Header */}
          <Box sx={{ p: theme.spacing(2), borderBottom: '1px solid rgba(0, 0, 0, 0.06)', position: 'relative', zIndex: 10 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: theme.spacing(2)
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <GlassCard
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',

                    border: 'none',
                  }}
                >
                 <img src={moxylogo} height={35} />
                </GlassCard>
              </Box>

              <IconButton
                onClick={toggleMobileDrawer}
                sx={{
                  background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
                  color: 'white',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 100%)`,
                    transform: 'rotate(180deg)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Close sx={{ fontSize: 18, color:"white" }} />
              </IconButton>
            </Box>

            {/* Profile */}
            <GlassCard
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                },
              }}
            >
              <Badge
                badgeContent={notificationsCount}
                color="error"
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{
                  '& .MuiBadge-badge': {
                    background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    minWidth: '16px',
                    height: '16px',
                  }
                }}
              >
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    boxShadow: `0 4px 14px ${alpha(primary, 0.4)}`,
                  }}
                >
                  {`${user?.first_name?.charAt(0)}${user?.last_name?.charAt(0)}`}
                </Avatar>
              </Badge>

              <Box sx={{ ml: 2, flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: '#2d3436',
                    lineHeight: 1.2,
                  }}
                >
                  {`${user?.first_name} ${user?.last_name}`}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#636e72',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                  }}
                >
                  {user?.role}
                </Typography>
              </Box>
            </GlassCard>
          </Box>

          {/* Scrollable Navigation Area */}
          <Box
            sx={{
              flex: 1,
              p: theme.spacing(2),
              overflowY: 'auto',
              overflowX: 'hidden',
              position: 'relative',
              zIndex: 10,
              '&::-webkit-scrollbar': {
                width: 4,
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: `linear-gradient(180deg, ${primary}, ${primaryDark})`,
                borderRadius: 2,
              },
            }}
          >
            {sidebarNavItems.map((item, index) => {
              const IconComponent = item.icon;
              const hasChildren = !!item.children;
              const isExpanded = expandedMenus[item.label];
              const isActive = item.path ? isItemActive(item.path) : false;
              const isParentWithActiveChild = isParentActive(item);

              return (
                <React.Fragment key={index}>
                  {/* Main Item */}
                  <Box
                    onClick={() => handleItemClick(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: '12px 16px',
                      mb: 1,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      position: 'relative',
                      background: isActive
                        ? `linear-gradient(135deg, ${primary} 0%, ${primaryLight} 100%)`
                        : isParentWithActiveChild && !item.path
                        ? alpha(primary, 0.08)
                        : 'transparent',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background: isActive
                          ? `linear-gradient(135deg, ${primary} 0%, ${primaryLight} 100%)`
                          : alpha(primary, 0.08),
                        transform: !isActive ? 'translateX(4px)' : 'none',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '3px',
                        height: isActive ? '70%' : '0%',
                        background: 'white',
                        borderRadius: '2px 0 0 2px',
                        transition: 'height 0.3s ease',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Box sx={{ position: 'relative' }}>
                        <IconComponent
                          sx={{
                            fontSize: 22,
                            color: isActive ? 'white' : primary,
                            mr: 2,
                            transition: 'all 0.3s ease',
                          }}
                        />
                        {item.badge && (
                          <Chip
                            label={item.badge}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -24,
                              height: '16px',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
                              color: 'white',
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: isActive ? 600 : 500,
                          fontSize: '0.9rem',
                          color: isActive ? 'white' : primary,
                          letterSpacing: '0.2px',
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>

                    {hasChildren && (
                      <Box
                        sx={{
                          ml: 'auto',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                        }}
                      >
                        <ExpandMore
                          sx={{
                            fontSize: 18,
                            color: isActive ? 'white' : primary,
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Children Items */}
                  {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ ml: 4, mt: 1 }}>
                        {item.children.map((child, childIndex) => {
                          const childActive = isItemActive(child.path);
                          return (
                            <Box
                              key={childIndex}
                              onClick={() => handleChildClick(child.path)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: '8px 16px',
                                mb: 0.5,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                position: 'relative',
                                background: childActive
                                  ? `linear-gradient(135deg, ${primary} 0%, ${primaryLight} 100%)`
                                  : 'transparent',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  background: childActive
                                    ? `linear-gradient(135deg, ${primary} 0%, ${primaryLight} 100%)`
                                    : alpha(primary, 0.08),
                                },
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 8,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: childActive ? '0' : '4px',
                                  height: childActive ? '0' : '4px',
                                  borderRadius: '50%',
                                  background: childActive ? 'transparent' : primaryLight,
                                },
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  right: 0,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: '2px',
                                  height: childActive ? '60%' : '0%',
                                  background: 'white',
                                  borderRadius: '1px 0 0 1px',
                                  transition: 'height 0.2s ease',
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  ml: 2,
                                  fontSize: '0.85rem',
                                  fontWeight: childActive ? 600 : 400,
                                  color: childActive ? 'white' : primary,
                                }}
                              >
                                {child.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: theme.spacing(2),
              borderTop: '1px solid rgba(0, 0, 0, 0.06)',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#636e72',
                textAlign: 'center',
                display: 'block',
              }}
            >
             Moxy Driving School © {new Date().getFullYear()}
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* Global Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.05);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.95);
          }
        }
      `}</style>
    </>
  );
};

export default MobileDrawer;
